import { flattenFoldersForPicker } from "@/lib/folders/folderTree";
import { findAureliaFolderForSlot } from "@/lib/mail/aureliaImapFolders";
import {
  NAV_FOLDER_LABELS,
  NAV_FOLDER_ORDER,
  navFolderSlot,
  pickBestFolderForSlot,
  type NavFolderSlot,
} from "@/lib/mail/folderNavSections";
import { AURELIA_SNOOZED_FOLDER } from "@/lib/snooze/constants";
import type { MailFolder } from "@/lib/types/mail";

export type FolderSlotImapStatusKind = "ok" | "ambiguous" | "missing" | "local";

export type FolderSlotImapStatus = {
  slot: NavFolderSlot;
  kind: FolderSlotImapStatusKind;
  /** Nom IMAP exact si résolu (même en ambre). */
  imapPath: string | null;
  /** Libellé court pour UI. */
  statusLabel: string;
};

const IMAP_SLOTS: NavFolderSlot[] = NAV_FOLDER_ORDER.filter((slot) => slot !== "outbox");

function isUsableHoldFolder(name: string, label: string): boolean {
  const slot = navFolderSlot(name, label);
  return slot !== "inbox" && slot !== "drafts" && slot !== "sent" && slot !== "trash" && slot !== "junk";
}

function candidatesForSlot(slot: NavFolderSlot, flat: MailFolder[]): MailFolder[] {
  if (slot === "inbox") {
    return flat.filter((folder) => folder.name.toUpperCase() === "INBOX");
  }
  const matches = flat.filter((folder) => navFolderSlot(folder.name, folder.label) === slot);
  if (slot === "snoozed") {
    return matches.filter((folder) => isUsableHoldFolder(folder.name, folder.label));
  }
  return matches.filter((folder) => folder.name.toUpperCase() !== "INBOX");
}

function findInFlat(flat: MailFolder[], name: string): MailFolder | undefined {
  return flat.find((folder) => folder.name === name);
}

/**
 * Statut de branchement IMAP d’un rôle nav (LIST locale — pas de SELECT serveur).
 */
export function resolveFolderSlotImapStatus(
  slot: NavFolderSlot,
  folders: MailFolder[],
  overrides: Partial<Record<NavFolderSlot, string>> = {}
): FolderSlotImapStatus {
  if (slot === "outbox") {
    return {
      slot,
      kind: "local",
      imapPath: null,
      statusLabel: "Local Aurélia",
    };
  }

  const flat = flattenFoldersForPicker(folders);
  const override = overrides[slot]?.trim() || "";

  const aurelia = findAureliaFolderForSlot(flat, slot);

  if (override) {
    if (override === AURELIA_SNOOZED_FOLDER) {
      return {
        slot,
        kind: "missing",
        imapPath: null,
        statusLabel: "Non branché",
      };
    }
    const match = findInFlat(flat, override);
    if (match) {
      // Override fournisseur alors qu’Aurelia/ existe → à rebrancher.
      if (aurelia && match.name !== aurelia.name) {
        return {
          slot,
          kind: "ambiguous",
          imapPath: aurelia.name,
          statusLabel: "Aurelia recommandé",
        };
      }
      return {
        slot,
        kind: "ok",
        imapPath: match.name,
        statusLabel: "Branché IMAP",
      };
    }
    return {
      slot,
      kind: "missing",
      imapPath: override,
      statusLabel: "Non branché",
    };
  }

  if (aurelia) {
    const candidates = candidatesForSlot(slot, flat);
    if (candidates.length > 1) {
      return {
        slot,
        kind: "ambiguous",
        imapPath: aurelia.name,
        statusLabel: "Aurelia recommandé",
      };
    }
    return {
      slot,
      kind: "ok",
      imapPath: aurelia.name,
      statusLabel: "Branché IMAP",
    };
  }

  const candidates = candidatesForSlot(slot, flat);
  if (candidates.length === 0) {
    return {
      slot,
      kind: "missing",
      imapPath: null,
      statusLabel: "Non branché",
    };
  }

  const best = pickBestFolderForSlot(slot, candidates) ?? candidates[0];
  if (candidates.length > 1) {
    return {
      slot,
      kind: "ambiguous",
      imapPath: best.name,
      statusLabel: "À confirmer",
    };
  }

  return {
    slot,
    kind: "ok",
    imapPath: best.name,
    statusLabel: "Branché IMAP",
  };
}

export function listFolderSlotImapStatuses(
  folders: MailFolder[],
  overrides: Partial<Record<NavFolderSlot, string>> = {}
): FolderSlotImapStatus[] {
  return NAV_FOLDER_ORDER.map((slot) => resolveFolderSlotImapStatus(slot, folders, overrides));
}

/** Rôles IMAP (hors outbox) qui ne sont pas verts. */
export function countFolderSlotsNeedingAttention(
  folders: MailFolder[],
  overrides: Partial<Record<NavFolderSlot, string>> = {}
): number {
  return IMAP_SLOTS.filter((slot) => {
    const status = resolveFolderSlotImapStatus(slot, folders, overrides);
    return status.kind === "missing" || status.kind === "ambiguous";
  }).length;
}

export function allImapFolderSlotsOk(
  folders: MailFolder[],
  overrides: Partial<Record<NavFolderSlot, string>> = {}
): boolean {
  return countFolderSlotsNeedingAttention(folders, overrides) === 0;
}

/** Sent / En attente : rôles critiques pour le rappel post-connexion. */
export function criticalFolderSlotsNeedAttention(
  folders: MailFolder[],
  overrides: Partial<Record<NavFolderSlot, string>> = {}
): boolean {
  for (const slot of ["sent", "snoozed"] as const) {
    const status = resolveFolderSlotImapStatus(slot, folders, overrides);
    if (status.kind === "missing" || status.kind === "ambiguous") return true;
  }
  return false;
}

const NUDGE_LS_PREFIX = "mail_folder_imap_nudge_seen_v1:";

export function hasSeenFolderImapNudge(accountId: number): boolean {
  try {
    return localStorage.getItem(`${NUDGE_LS_PREFIX}${accountId}`) === "1";
  } catch {
    return true;
  }
}

export function markFolderImapNudgeSeen(accountId: number): void {
  try {
    localStorage.setItem(`${NUDGE_LS_PREFIX}${accountId}`, "1");
  } catch {
    /* ignore */
  }
}

export function folderSlotStatusSummaryLabel(
  folders: MailFolder[],
  overrides: Partial<Record<NavFolderSlot, string>> = {}
): string {
  const needing = countFolderSlotsNeedingAttention(folders, overrides);
  if (needing === 0) return "Tous les rôles IMAP sont branchés";
  return needing === 1
    ? "1 rôle à vérifier"
    : `${needing} rôles à vérifier`;
}

export function folderSlotDisplayName(slot: NavFolderSlot): string {
  return NAV_FOLDER_LABELS[slot];
}
